/**
 * Pure helpers behind the parties panel's add/edit form: the editable
 * draft shape, prefill from a Bolagsverket lookup, and turning edits into
 * the `save_draft` / `ir_preview` payloads. No React, no network.
 */

import {
  classifyParty,
  type PartyIdType as ClassifierIdType,
} from '@regta/common/classify-party';
import type { PartyIdType, PartySearchHit, PartySnapshotInput } from '../api.js';
import type { LocalParty } from '../components/PartiesPanel.js';

export type PartyDraft = {
  id_type: PartyIdType;
  id_number: string;
  legal_name: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  postal_code: string;
  city: string;
  country: string;
};

export const EMPTY_PARTY_DRAFT: PartyDraft = {
  id_type: 'org_no',
  id_number: '',
  legal_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'SE',
};

function orNull(s: string): string | null {
  const t = s.trim();
  return t.length > 0 ? t : null;
}

function countryOrNull(s: string): string | null {
  const t = s.trim().toUpperCase();
  return t.length === 2 ? t : null;
}

export function draftFromParty(p: LocalParty): PartyDraft {
  return {
    id_type: p.id_type,
    id_number: p.id_number ?? '',
    legal_name: p.legal_name,
    email: p.email ?? '',
    phone: p.phone ?? '',
    address_line1: p.address_line1 ?? '',
    address_line2: p.address_line2 ?? '',
    postal_code: p.postal_code ?? '',
    city: p.city ?? '',
    country: p.country ?? 'SE',
  };
}

/** Snapshot fields of a draft, trimmed, empty → null. */
export function draftSnapshot(draft: PartyDraft) {
  return {
    id_type: draft.id_type,
    id_number: orNull(draft.id_number),
    legal_name: draft.legal_name.trim(),
    email: orNull(draft.email),
    phone: orNull(draft.phone),
    address_line1: orNull(draft.address_line1),
    address_line2: orNull(draft.address_line2),
    postal_code: orNull(draft.postal_code),
    city: orNull(draft.city),
    country: countryOrNull(draft.country),
  };
}

/**
 * Fill the draft's EMPTY fields from an org-number lookup hit. Anything the
 * user already typed wins; the user can still edit the result.
 */
export function fillDraftFromLookup(
  draft: PartyDraft,
  hit: Pick<
    PartySearchHit,
    'legalName' | 'email' | 'phone' | 'addressLine1' | 'addressLine2' | 'postalCode' | 'city' | 'country'
  >,
): PartyDraft {
  const keep = (cur: string, next: string | null | undefined) =>
    cur.trim() ? cur : (next ?? '');
  const hasAddress = Boolean(
    draft.address_line1.trim() || draft.postal_code.trim() || draft.city.trim(),
  );
  return {
    ...draft,
    legal_name: keep(draft.legal_name, hit.legalName),
    email: keep(draft.email, hit.email),
    phone: keep(draft.phone, hit.phone),
    // The address is filled as a unit so a half-typed one is never mixed
    // with the registry's.
    ...(hasAddress
      ? {}
      : {
          address_line1: hit.addressLine1 ?? '',
          address_line2: hit.addressLine2 ?? '',
          postal_code: hit.postalCode ?? '',
          city: hit.city ?? '',
          country: hit.country ?? draft.country,
        }),
  };
}

/** Apply an edit draft to a seated party; persisted rows are marked dirty. */
export function applyPartyDraft(party: LocalParty, draft: PartyDraft): LocalParty {
  const snap = draftSnapshot(draft);
  const { privacy_class } = classifyParty({
    id_type: snap.id_type as ClassifierIdType,
    id_number: snap.id_number,
  });
  return {
    ...party,
    ...snap,
    privacy_class,
    ...(party._persistedId ? { _dirty: true } : {}),
  };
}

export function toPartySnapshotInput(p: LocalParty): PartySnapshotInput {
  return {
    role: p.role,
    ordinal: p.ordinal,
    id_type: p.id_type,
    id_number: p.id_number,
    legal_name: p.legal_name,
    display_name: p.display_name,
    email: p.email,
    phone: p.phone,
    address_line1: p.address_line1,
    address_line2: p.address_line2,
    postal_code: p.postal_code,
    city: p.city,
    country: p.country,
    provenance: p.provenance,
  };
}

/** `save_draft.parties_update` for edited persisted parties. */
export function partiesUpdatePayload(
  parties: ReadonlyArray<LocalParty>,
): Array<Partial<PartySnapshotInput> & { id: string }> {
  return parties
    .filter((p) => p._persistedId && p._dirty)
    .map((p) => {
      const { role: _role, ordinal: _ordinal, provenance: _prov, ...rest } =
        toPartySnapshotInput(p);
      return { id: p._persistedId!, ...rest };
    });
}

/**
 * Unsaved party changes as an `ir_preview` overlay. The preview has no
 * update channel, so an edited persisted party rides as remove + add of
 * the same slot (compile input only — nothing is written).
 */
export function previewPartyOverlay(
  parties: ReadonlyArray<LocalParty>,
  removedIds: ReadonlyArray<string>,
): { add: PartySnapshotInput[]; remove: string[] } {
  const edited = parties.filter((p) => p._persistedId && p._dirty);
  return {
    add: [...parties.filter((p) => !p._persistedId), ...edited].map(toPartySnapshotInput),
    remove: [...removedIds, ...edited.map((p) => p._persistedId!)],
  };
}
